import { TFunction } from 'i18next';
import {
  PollInMessage,
  PollOption,
  PollResultsOption,
  PollVoteStatsResponse,
} from 'types/pollTypes';
import { pluralize } from 'utils/text.utils';

export const pollApiPaths = {
  answer: (pollId: string): string => `/poll/${pollId}/answer`,
  answerUsers: (pollId: string): string => `/poll/${pollId}/answer-users`,
  create: '/poll',
  get: (pollId: string): string => `/poll/${pollId}`,
  stop: (pollId: string): string => `/poll/${pollId}/stop`,
};

export const isPollStopped = (poll: PollInMessage): boolean => poll.a === false;

export const isPollActive = (poll: PollInMessage): boolean => poll.a !== false;

export const isPollMultiple = (poll: PollInMessage): boolean => poll.m === true;

export const isPollQuiz = (poll: PollInMessage): boolean => poll.q === true;

export const formatPollVotesCount = (count: number | undefined, t: TFunction): string =>
  pluralize(count ?? 0, [t('poll.votes.count.w1'), t('poll.votes.count.w2'), t('poll.votes.count.w5')], true);

export type QuizOptionStyle = 'correct' | 'neutral' | 'wrong';

export const getQuizOptionStyle = (
  isQuiz: boolean,
  isStopped: boolean,
  option: PollOption,
  isUserChoice: boolean
): QuizOptionStyle | null => {
  if (!isQuiz || !isStopped) return null;
  if (option.r) return 'correct';
  if (isUserChoice) return 'wrong';
  return 'neutral';
};

export const sortPollOptions = (options: PollOption[]): PollOption[] =>
  [...options].sort((a, b) => a.ord - b.ord);

export const sortPollResultsOptions = (options: PollResultsOption[]): PollResultsOption[] =>
  [...options].sort((a, b) => a.ord - b.ord);

export const mergePollVoteStats = (
  poll: PollInMessage,
  stats: PollVoteStatsResponse | null | undefined,
  optionIds: string[]
): PollInMessage => {
  if (!stats?.oo?.length) {
    return { ...poll, aa: optionIds };
  }

  const patchById = new Map(stats.oo.map((option) => [option.i, option]));

  return {
    ...poll,
    aa: optionIds,
    cv: stats.cv ?? poll.cv,
    oo: poll.oo.map((option) => {
      const patch = patchById.get(option.i);
      if (!patch) {
        return option;
      }

      return {
        ...option,
        pv: patch.pv,
        ...('r' in patch ? { r: Boolean(patch.r) } : {}),
        ...('d' in patch ? { d: patch.d } : {}),
      };
    }),
  };
};

export const updatePollInChatInfo = <T extends { poll?: PollInMessage; id: number }>(
  chatInfo: T[],
  pollId: string,
  pollData: PollInMessage
): T[] => chatInfo.map((message) => (message.poll?.i === pollId ? { ...message, poll: pollData } : message));
